import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChatCircle, PaperPlaneTilt, Paperclip, Users, Plus, MagnifyingGlass, X,
  Image as ImageIcon, File as FileIcon, Trash, UserPlus,
  VideoCamera, Phone, Monitor,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { fmtIstChatTime } from '../lib/datetime';
import { makeMeetingUrl, parseCallCard, CALL_MODE_LABEL, CALL_MODE_BG, CALL_MODE_FG } from '../lib/meeting';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = fmtIstChatTime;

const initials = (name = '?') => name.split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// Detect URLs (http://, https://, www.) and bare emails in a message text and turn them into clickable links.
// Splits on the URL regex while keeping the matches in place. Tested against punctuation trailing the URL.
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+|[\w.+-]+@[\w-]+\.[\w.-]+)/gi;
const Linkify = ({ text, mine }) => {
  if (!text) return null;
  const parts = String(text).split(URL_REGEX);
  const linkClass = mine
    ? 'underline text-white hover:text-emerald-100 break-all'
    : 'underline text-emerald-700 hover:text-emerald-800 break-all';
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return part;
        // Strip trailing punctuation that the regex over-captured (e.g. period at sentence end)
        const m = part.match(/^(.*?)([.,;:!?)\]]*)$/);
        const link = m ? m[1] : part;
        const tail = m ? m[2] : '';
        const href = link.startsWith('http')
          ? link
          : (link.includes('@') && !link.includes('/'))
            ? `mailto:${link}`
            : `https://${link}`;
        return (
          <React.Fragment key={i}>
            <a href={href} target="_blank" rel="noreferrer" className={linkClass} data-testid="chat-link">{link}</a>
            {tail}
          </React.Fragment>
        );
      })}
    </>
  );
};

// Avatar component — shows profile picture if available, else colored initials
const Avatar = ({ name, picture, size = 36, bg = '#1D9E75', icon = null, testId = '' }) => {
  const style = { width: size, height: size, background: bg, fontSize: Math.round(size * 0.32) };
  if (picture) {
    return (
      <img
        src={picture}
        alt={name || ''}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
      style={style}
      data-testid={testId}
    >
      {icon || initials(name)}
    </div>
  );
};

export default function ChatPage() {
  const { user } = useAuth();
  const meId = user?.id || user?._id;
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newGroupMode, setNewGroupMode] = useState(false);
  const [pickedMembers, setPickedMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [showRoomMembersPanel, setShowRoomMembersPanel] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [pickedAddMembers, setPickedAddMembers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const loadRooms = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/chat/rooms`, { withCredentials: true });
      setRooms(r.data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      console.error('Failed to load chat rooms:', e);
      toast.error('Chat rooms failed: ' + msg);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/chat/users`, { withCredentials: true });
      setAllUsers(r.data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      console.error('Failed to load chat users:', e);
      toast.error('Users list failed: ' + msg + ' — admin may need to run "Bulk Create Logins"');
    }
    setUsersLoading(false);
  }, []);

  const sendBroadcast = async () => {
    if (!broadcastText.trim()) return toast.error('Please type a message');
    if (!window.confirm(`Send this announcement to ALL employees with a login?\n\n"${broadcastText.trim().slice(0, 120)}${broadcastText.length > 120 ? '…' : ''}"`)) return;
    const textToSend = broadcastText.trim();
    setBroadcastSending(true);
    // Close dialog immediately for snappy UX — toast will confirm success/failure
    setBroadcastOpen(false);
    setBroadcastText('');
    try {
      const r = await axios.post(`${API_URL}/api/chat/broadcast`, { text: textToSend }, { withCredentials: true });
      toast.success(r.data.message || 'Broadcast sent');
      await loadRooms();
      // Jump to the announcements room
      const rooms2 = (await axios.get(`${API_URL}/api/chat/rooms`, { withCredentials: true })).data || [];
      const ann = rooms2.find((rm) => rm.type === 'announcements');
      if (ann) setActiveRoom(ann);
    } catch (e) {
      toast.error('Broadcast failed: ' + (e.response?.data?.detail || e.message));
      // Re-open dialog with the user's text so they don't lose it
      setBroadcastText(textToSend);
      setBroadcastOpen(true);
    }
    setBroadcastSending(false);
  };

  useEffect(() => { loadRooms(); loadUsers(); }, [loadRooms, loadUsers]);

  // Load messages whenever active room changes
  useEffect(() => {
    if (!activeRoom) { setMessages([]); return; }
    let cancelled = false;
    const fetchMsgs = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, { withCredentials: true });
        if (!cancelled) setMessages(r.data || []);
      } catch (e) { /* room may have been removed */ }
    };
    fetchMsgs();
    // Mark this room as read on open — resets the unread badge for this room
    (async () => {
      try {
        await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/mark-read`, {}, { withCredentials: true });
        // Tell the topbar ChatBell to refresh its count
        window.dispatchEvent(new CustomEvent('chat-read', { detail: { roomId: activeRoom.id } }));
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeRoom?.id]); // eslint-disable-line

  // Lightweight polling — refresh active room messages + rooms list every 4s
  useEffect(() => {
    const id = setInterval(async () => {
      if (activeRoom) {
        try {
          const r = await axios.get(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, { withCredentials: true });
          setMessages(r.data || []);
          // Keep this room marked as read while the user is actively in it
          await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/mark-read`, {}, { withCredentials: true });
        } catch { /* ignore */ }
      }
      try {
        const r = await axios.get(`${API_URL}/api/chat/rooms`, { withCredentials: true });
        setRooms(r.data || []);
        window.dispatchEvent(new CustomEvent('chat-read'));
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(id);
  }, [activeRoom?.id]); // eslint-disable-line

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, activeRoom?.id]);

  const filteredRooms = rooms.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q)
      || (r.member_names || []).some((n) => (n || '').toLowerCase().includes(q))
      || (r.last_message_preview || '').toLowerCase().includes(q);
  });

  const roomDisplayName = (r) => {
    if (!r) return '';
    if (r.type === 'group' || r.type === 'announcements') return r.name || 'Group';
    // direct: show the other member's name
    const idx = (r.members || []).findIndex((m) => m !== meId);
    return (r.member_names || [])[idx] || 'Direct chat';
  };

  // Pick the "other" person's picture in a direct chat (or empty for groups)
  const roomDisplayPicture = (r) => {
    if (!r) return '';
    if (r.type === 'announcements' || r.type === 'group') return '';
    const idx = (r.members || []).findIndex((m) => m !== meId);
    return (r.member_pictures || [])[idx] || '';
  };

  const togglePickedMember = (id) => {
    setPickedMembers((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const startNewChat = async () => {
    if (pickedMembers.length === 0) return toast.error('Please pick at least one member');
    if (newGroupMode && pickedMembers.length < 2) return toast.error('Group needs at least 2 other members');
    try {
      const body = {
        type: newGroupMode ? 'group' : 'direct',
        members: pickedMembers,
        name: newGroupMode ? (groupName.trim() || '') : '',
      };
      const r = await axios.post(`${API_URL}/api/chat/rooms`, body, { withCredentials: true });
      setNewDialogOpen(false);
      setNewGroupMode(false);
      setPickedMembers([]);
      setGroupName('');
      await loadRooms();
      setActiveRoom(r.data);
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const onFilesPicked = async (files) => {
    if (!activeRoom || !files || files.length === 0) return;
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name}: too large (max 25 MB)`); continue; }
      const formData = new FormData();
      formData.append('file', f);
      try {
        const r = await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000,
          maxContentLength: 30 * 1024 * 1024,
          maxBodyLength: 30 * 1024 * 1024,
        });
        setPendingAttachments((cur) => [...cur, r.data]);
      } catch (e) {
        const detail = e.response?.data?.detail || e.response?.statusText || e.message || 'unknown error';
        const code = e.response?.status ? `[${e.response.status}] ` : '';
        toast.error(`${f.name}: ${code}${detail}`);
      }
    }
  };

  const send = async () => {
    if (!activeRoom) return;
    if (!composer.trim() && pendingAttachments.length === 0) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, {
        text: composer.trim(), attachments: pendingAttachments,
      }, { withCredentials: true });
      setComposer(''); setPendingAttachments([]);
      // immediate refresh
      const r = await axios.get(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, { withCredentials: true });
      setMessages(r.data || []);
      loadRooms();
    } catch (e) { toast.error('Send failed: ' + (e.response?.data?.detail || e.message)); }
    setSending(false);
  };

  const deleteMessage = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/messages/${m.id}`, { withCredentials: true });
      setMessages((cur) => cur.map((x) => x.id === m.id ? { ...x, deleted: true, text: '' } : x));
    } catch (e) { toast.error('Delete failed'); }
  };

  // Start a video/voice/screen-share call. Generates a unique Jitsi room, posts a
  // call card into the chat (so the other side sees the "Join Call" button), and
  // opens the room in a new tab for the caller.
  const startCall = async (mode) => {
    if (!activeRoom) return;
    const url = makeMeetingUrl({ prefix: 'chat', contextId: activeRoom.id, mode });
    const emoji = mode === 'voice' ? '📞' : mode === 'screen' ? '🖥️' : '📹';
    const verb = mode === 'voice' ? 'voice call' : mode === 'screen' ? 'screen-share session' : 'video call';
    const text = `${emoji} Started a ${verb} — Join here: ${url}`;
    try {
      await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, {
        text, attachments: [],
      }, { withCredentials: true });
      // Refresh messages so the caller sees the card immediately
      const r = await axios.get(`${API_URL}/api/chat/rooms/${activeRoom.id}/messages`, { withCredentials: true });
      setMessages(r.data || []);
      loadRooms();
      // Open the call for the caller in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`${CALL_MODE_LABEL[mode]} started — link posted to chat`);
    } catch (e) {
      toast.error('Could not start call: ' + (e.response?.data?.detail || e.message));
    }
  };

  const addMembers = async () => {
    if (!activeRoom || pickedAddMembers.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/chat/rooms/${activeRoom.id}/members`, { members: pickedAddMembers }, { withCredentials: true });
      toast.success('Members added');
      setAddMemberOpen(false); setPickedAddMembers([]);
      await loadRooms();
      const updated = (await axios.get(`${API_URL}/api/chat/rooms`, { withCredentials: true })).data.find((r) => r.id === activeRoom.id);
      setActiveRoom(updated || activeRoom);
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm('Remove this member from the group?')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/rooms/${activeRoom.id}/members/${uid}`, { withCredentials: true });
      toast.success('Member removed');
      await loadRooms();
      const updated = (await axios.get(`${API_URL}/api/chat/rooms`, { withCredentials: true })).data.find((r) => r.id === activeRoom.id);
      setActiveRoom(updated || null);
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const isImg = (a) => (a.mime_type || '').startsWith('image/');
  const fileUrl = (a) => `${API_URL}${a.url}`;

  return (
    <div className="h-[calc(100vh-110px)] flex gap-0 border rounded-lg overflow-hidden" data-testid="chat-page" style={{ background: '#ffffff' }}>
      {/* Left: rooms list */}
      <div className="w-72 border-r flex flex-col" style={{ background: '#f8fafc' }}>
        <div className="px-3 py-2 border-b flex items-center justify-between" style={{ background: '#1a2e4a' }}>
          <h2 className="text-sm font-bold text-white flex items-center gap-1.5">
            <ChatCircle size={16} weight="fill" /> Internal Chat
          </h2>
          <div className="flex items-center gap-1">
            {user?.role === 'admin' && (
              <Button size="sm" variant="ghost" onClick={() => setBroadcastOpen(true)} className="text-white hover:bg-white/10 h-7 px-2" title="Broadcast to all employees" data-testid="broadcast-btn">
                📢
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setNewDialogOpen(true)} className="text-white hover:bg-white/10 h-7 w-7 p-0" data-testid="new-chat-btn">
              <Plus size={14} />
            </Button>
          </div>
        </div>
        <div className="p-2 border-b">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-2.5 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search chats…" className="text-xs h-8 pl-7" data-testid="chat-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredRooms.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-500">
              {rooms.length === 0 ? 'No chats yet. Tap + to start one.' : 'No matches.'}
            </div>
          ) : filteredRooms.map((r) => {
            const isActive = activeRoom?.id === r.id;
            const display = roomDisplayName(r);
            const pic = roomDisplayPicture(r);
            const unread = Number(r.unread || 0);
            const isUnread = unread > 0 && !isActive;
            return (
              <button
                key={r.id} onClick={() => setActiveRoom(r)}
                className={`w-full text-left px-3 py-2 border-b hover:bg-gray-50 ${isActive ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''}`}
                data-testid={`chat-room-${r.id}`}
              >
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Avatar
                      name={display}
                      picture={pic}
                      size={36}
                      bg={r.type === 'announcements' ? '#dc2626' : r.type === 'group' ? '#7c3aed' : '#1D9E75'}
                      icon={r.type === 'announcements' ? '📢' : r.type === 'group' ? <Users size={14} weight="fill" /> : null}
                      testId={`room-avatar-${r.id}`}
                    />
                    {isUnread && (
                      <span
                        className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white"
                        style={{ background: '#dc2626' }}
                        data-testid={`room-unread-${r.id}`}
                      >
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs truncate ${isUnread ? 'font-extrabold' : 'font-bold'}`} style={{ color: '#1a2e4a' }}>
                      {display} {r.type === 'group' && <span className="text-[9px] text-gray-400 font-normal">({(r.members || []).length})</span>}
                    </div>
                    <div className={`text-[10px] truncate ${isUnread ? 'text-emerald-700 font-semibold' : 'text-gray-500'}`}>{r.last_message_preview || <em>(no messages)</em>}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: chat panel */}
      <div className="flex-1 flex flex-col" style={{ background: '#fafafa' }}>
        {!activeRoom ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
            <ChatCircle size={48} weight="duotone" className="mb-3" />
            <p className="text-sm">Pick a chat on the left to start messaging</p>
            <Button size="sm" onClick={() => setNewDialogOpen(true)} className="mt-4" data-testid="new-chat-empty-btn">
              <Plus size={14} className="mr-1" /> New Chat
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 py-2.5 border-b flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                <Avatar
                  name={roomDisplayName(activeRoom)}
                  picture={roomDisplayPicture(activeRoom)}
                  size={36}
                  bg={activeRoom.type === 'announcements' ? '#dc2626' : activeRoom.type === 'group' ? '#7c3aed' : '#1D9E75'}
                  icon={activeRoom.type === 'announcements' ? '📢' : activeRoom.type === 'group' ? <Users size={14} weight="fill" /> : null}
                  testId="chat-header-avatar"
                />
                <div>
                  <div className="text-sm font-bold" style={{ color: '#1a2e4a' }}>{roomDisplayName(activeRoom)}</div>
                  <div className="text-[10px] text-gray-500">
                    {activeRoom.type === 'announcements'
                      ? `${(activeRoom.members || []).length} employees · Read-only for everyone except admin`
                      : activeRoom.type === 'group'
                      ? `${(activeRoom.members || []).length} members · ${(activeRoom.member_names || []).slice(0, 4).join(', ')}${(activeRoom.members || []).length > 4 ? '…' : ''}`
                      : (activeRoom.member_names || []).find((n, i) => activeRoom.members[i] !== meId)}
                  </div>
                </div>
              </div>
              {activeRoom.type !== 'announcements' && (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => startCall('voice')}
                    title="Start voice call"
                    data-testid="call-voice-btn"
                    className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                  >
                    <Phone size={16} weight="fill" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => startCall('video')}
                    title="Start video call"
                    data-testid="call-video-btn"
                    className="text-emerald-600 hover:bg-emerald-50 h-8 w-8 p-0"
                  >
                    <VideoCamera size={16} weight="fill" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => startCall('screen')}
                    title="Start screen-share"
                    data-testid="call-screen-btn"
                    className="text-amber-600 hover:bg-amber-50 h-8 w-8 p-0"
                  >
                    <Monitor size={16} weight="fill" />
                  </Button>
                  {activeRoom.type === 'group' && (
                    <Button size="sm" variant="ghost" onClick={() => setShowRoomMembersPanel((s) => !s)} data-testid="toggle-members-panel" title="Members" className="ml-1">
                      <Users size={14} />
                    </Button>
                  )}
                </div>
              )}
              {activeRoom.type === 'announcements' && activeRoom.type === 'group' && (
                <Button size="sm" variant="ghost" onClick={() => setShowRoomMembersPanel((s) => !s)} data-testid="toggle-members-panel" title="Members">
                  <Users size={14} />
                </Button>
              )}
            </div>

            {/* Members panel (collapsible) */}
            {showRoomMembersPanel && activeRoom.type === 'group' && (
              <div className="px-4 py-2 border-b bg-slate-50 text-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-bold text-slate-700">Group Members</div>
                  <Button size="sm" variant="outline" onClick={() => setAddMemberOpen(true)} className="h-7 text-xs" data-testid="add-member-btn">
                    <UserPlus size={12} className="mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(activeRoom.members || []).map((mid, i) => {
                    const name = activeRoom.member_names?.[i] || mid.slice(-6);
                    const pic = activeRoom.member_pictures?.[i] || '';
                    const isCreator = activeRoom.created_by === mid;
                    const isMe = mid === meId;
                    return (
                      <span key={mid} className="inline-flex items-center gap-1 text-[10px] font-semibold pl-1 pr-2 py-0.5 rounded-full bg-white border">
                        <Avatar name={name} picture={pic} size={18} bg="#1D9E75" />
                        {name}
                        {isCreator && <span className="text-[8px] text-amber-600">★</span>}
                        {isMe && <span className="text-[8px] text-emerald-600">(you)</span>}
                        {!isMe && (activeRoom.created_by === meId || user?.role === 'admin') && (
                          <button onClick={() => removeMember(mid)} className="ml-1 text-rose-500 hover:text-rose-700" title="Remove"><X size={10} /></button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-list">
              {messages.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-8">Start the conversation</div>
              ) : messages.map((m) => {
                const mine = m.sender_id === meId;
                const callCard = !m.deleted ? parseCallCard(m.text || '') : null;
                return (
                  <div key={m.id} className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                    {!mine && (
                      <Avatar
                        name={m.sender_name || ''}
                        picture={m.sender_picture || ''}
                        size={28}
                        bg="#1D9E75"
                        testId={`msg-avatar-${m.id}`}
                      />
                    )}
                    <div className={`max-w-[70%] ${mine ? 'order-2' : ''}`}>
                      {callCard ? (
                        // Call invitation card — styled prominently with a Join button
                        <div
                          className="rounded-2xl px-3.5 py-3 shadow-sm border-2"
                          style={{ background: CALL_MODE_BG[callCard.mode], borderColor: CALL_MODE_FG[callCard.mode] + '40' }}
                          data-testid={`call-card-${m.id}`}
                        >
                          {!mine && (
                            <div className="text-[10px] font-bold mb-1" style={{ color: CALL_MODE_FG[callCard.mode] }}>
                              {m.sender_name}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl leading-none" aria-hidden>{callCard.emoji}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold" style={{ color: CALL_MODE_FG[callCard.mode] }}>
                                {mine ? 'You started a' : `${m.sender_name || 'Someone'} started a`} {CALL_MODE_LABEL[callCard.mode]}
                              </div>
                              <div className="text-[10px] text-gray-600">Tap the button to join from any browser</div>
                            </div>
                          </div>
                          <a
                            href={callCard.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-1.5 w-full text-white text-xs font-bold rounded-lg px-3 py-2 transition-colors"
                            style={{ background: CALL_MODE_FG[callCard.mode] }}
                            data-testid={`call-join-${m.id}`}
                          >
                            {callCard.mode === 'voice' ? <Phone size={14} weight="fill" />
                              : callCard.mode === 'screen' ? <Monitor size={14} weight="fill" />
                              : <VideoCamera size={14} weight="fill" />}
                            Join {CALL_MODE_LABEL[callCard.mode]}
                          </a>
                          <div className="text-[9px] text-gray-500 mt-1.5 break-all opacity-70">{callCard.url}</div>
                        </div>
                      ) : (
                      <div className={`rounded-2xl px-3.5 py-2 shadow-sm ${mine
                        ? 'bg-emerald-600 text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 rounded-bl-sm'} ${m.deleted ? 'italic opacity-60' : ''}`}>
                        {!mine && (
                          <div className="text-[10px] font-bold mb-0.5" style={{ color: '#1D9E75' }}>{m.sender_name}</div>
                        )}
                        {m.deleted ? (
                          <span className="text-xs">(message deleted)</span>
                        ) : (
                          <>
                            {m.text && <div className="text-sm whitespace-pre-wrap break-words"><Linkify text={m.text} mine={mine} /></div>}
                            {(m.attachments || []).map((a, i) => (
                              <div key={i} className="mt-1.5">
                                {isImg(a) ? (
                                  <a href={fileUrl(a)} target="_blank" rel="noreferrer">
                                    <img src={fileUrl(a)} alt={a.filename} className="max-w-full max-h-72 rounded border" />
                                  </a>
                                ) : (
                                  <a href={fileUrl(a)} target="_blank" rel="noreferrer"
                                    className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${mine ? 'bg-emerald-500/30 border-emerald-300 text-white' : 'bg-gray-50'}`}>
                                    <FileIcon size={14} />
                                    <div className="flex-1 min-w-0">
                                      <div className="truncate font-semibold">{a.filename}</div>
                                      <div className={`text-[9px] ${mine ? 'text-emerald-100' : 'text-gray-500'}`}>{Math.round((a.size || 0) / 1024)} KB</div>
                                    </div>
                                  </a>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                      )}
                      <div className={`flex items-center gap-2 mt-0.5 ${mine ? 'justify-end' : ''}`}>
                        <span className="text-[9px] text-gray-400">{fmtTime(m.created_at)}</span>
                        {mine && !m.deleted && (
                          <button onClick={() => deleteMessage(m)} className="text-[9px] text-gray-400 hover:text-rose-500" data-testid={`del-msg-${m.id}`}>delete</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            {(activeRoom.type !== 'announcements' || user?.role === 'admin') ? (
              <div className="border-t bg-white p-2">
              {pendingAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 px-2">
                  {pendingAttachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid={`pending-att-${i}`}>
                      {isImg(a) ? <ImageIcon size={11} /> : <FileIcon size={11} />}
                      {a.filename}
                      <button onClick={() => setPendingAttachments((cur) => cur.filter((_, idx) => idx !== i))} className="ml-1 hover:text-rose-600"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                  onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ''; }}
                  data-testid="chat-file-input"
                />
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} title="Attach file" data-testid="attach-btn">
                  <Paperclip size={14} />
                </Button>
                <textarea
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message…  (Shift+Enter for new line)"
                  rows={1}
                  className="flex-1 border rounded px-3 py-2 text-sm min-h-[36px] max-h-32 resize-y"
                  data-testid="chat-composer"
                />
                <Button onClick={send} disabled={sending || (!composer.trim() && pendingAttachments.length === 0)} data-testid="chat-send-btn">
                  <PaperPlaneTilt size={14} className="mr-1" weight="fill" /> Send
                </Button>
              </div>
            </div>
            ) : (
              <div className="border-t bg-amber-50 p-3 text-center text-xs text-amber-800" data-testid="announcements-readonly">
                <strong>📢 Read-only Announcements channel</strong> — only admin can post here.
              </div>
            )}
          </>
        )}
      </div>

      {/* New Chat / New Group dialog */}
      {newDialogOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setNewDialogOpen(false)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="new-chat-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>{newGroupMode ? 'Create Group' : 'New Chat'}</h3>
              <button onClick={() => setNewDialogOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => { setNewGroupMode(false); setPickedMembers([]); }}
                className={`flex-1 text-xs px-3 py-2 rounded border-2 ${!newGroupMode ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold' : 'border-gray-200'}`}
                data-testid="mode-direct"
              >Direct Chat</button>
              <button
                onClick={() => { setNewGroupMode(true); setPickedMembers([]); }}
                className={`flex-1 text-xs px-3 py-2 rounded border-2 ${newGroupMode ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-gray-200'}`}
                data-testid="mode-group"
              >Group Chat</button>
            </div>
            {newGroupMode && (
              <div className="mb-3">
                <Label className="text-xs">Group Name</Label>
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="e.g. WordPress Team" data-testid="group-name-input" />
              </div>
            )}
            <Label className="text-xs">{newGroupMode ? 'Members (pick 2 or more)' : 'Pick a person'}</Label>
            <div className="mt-1 mb-1.5 relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={newChatSearch}
                onChange={(e) => setNewChatSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="pl-8 h-8 text-xs"
                autoFocus
                data-testid="new-chat-user-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              {usersLoading ? (
                <div className="p-4 text-center text-xs text-gray-400">Loading users…</div>
              ) : allUsers.length === 0 ? (
                <div className="p-4 text-center text-xs text-rose-700 bg-rose-50">
                  <strong>No other employees have a login yet.</strong>
                  <div className="mt-1 text-gray-600">Ask admin to run <em>&ldquo;Bulk Create Logins&rdquo;</em> on the Employees page.</div>
                </div>
              ) : (() => {
                const q = newChatSearch.trim().toLowerCase();
                const list = q
                  ? allUsers.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q))
                  : allUsers;
                if (list.length === 0) return <div className="p-4 text-center text-xs text-gray-400">No matches for &ldquo;{newChatSearch}&rdquo;.</div>;
                return list.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0">
                  <input
                    type={newGroupMode ? 'checkbox' : 'radio'}
                    name="new-chat-member"
                    checked={pickedMembers.includes(u.id)}
                    onChange={() => newGroupMode ? togglePickedMember(u.id) : setPickedMembers([u.id])}
                    data-testid={`new-member-${u.id}`}
                  />
                  <Avatar name={u.name} picture={u.profile_picture} size={28} bg="#1D9E75" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{u.name}</div>
                    <div className="text-[10px] text-gray-500 truncate">{u.email}</div>
                  </div>
                </label>
                ));
              })()}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setNewDialogOpen(false)}>Cancel</Button>
              <Button onClick={startNewChat} disabled={pickedMembers.length === 0} data-testid="start-new-chat-btn">
                Start
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add member to existing group */}
      {addMemberOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAddMemberOpen(false)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="add-member-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>Add Members</h3>
              <button onClick={() => setAddMemberOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="mb-2 relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={addMemberSearch}
                onChange={(e) => setAddMemberSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="pl-8 h-8 text-xs"
                autoFocus
                data-testid="add-member-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              {(() => {
                const q = addMemberSearch.trim().toLowerCase();
                const list = allUsers.filter((u) => !(activeRoom?.members || []).includes(u.id))
                  .filter((u) => !q || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q));
                if (list.length === 0) return <div className="p-4 text-center text-xs text-gray-400">{q ? `No matches for "${addMemberSearch}".` : 'All users are already in this group.'}</div>;
                return list.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0">
                  <input
                    type="checkbox"
                    checked={pickedAddMembers.includes(u.id)}
                    onChange={() => setPickedAddMembers((cur) => cur.includes(u.id) ? cur.filter((x) => x !== u.id) : [...cur, u.id])}
                    data-testid={`add-cb-${u.id}`}
                  />
                  <Avatar name={u.name} picture={u.profile_picture} size={24} bg="#1D9E75" />
                  <span className="text-xs">{u.name}</span>
                  <span className="text-[9px] text-gray-400 ml-auto">{u.email}</span>
                </label>
                ));
              })()}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
              <Button onClick={addMembers} disabled={pickedAddMembers.length === 0} data-testid="add-members-confirm">Add</Button>
            </div>
          </Card>
        </div>
      )}
      {/* Broadcast dialog */}
      {broadcastOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBroadcastOpen(false)}>
          <Card className="w-full max-w-lg bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="broadcast-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
                📢 Broadcast Announcement
              </h3>
              <button onClick={() => setBroadcastOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              This message will be posted to the <strong>📢 Announcements</strong> channel — visible to every employee with a login. They cannot reply to this channel.
            </p>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm min-h-[120px]"
              placeholder="Type your announcement here…"
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              data-testid="broadcast-text"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
              <Button onClick={sendBroadcast} disabled={broadcastSending || !broadcastText.trim()} data-testid="broadcast-send-btn">
                {broadcastSending ? 'Sending…' : 'Send to All Employees'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
